/* ==========================================================================
   Sticky add to cart bar — behaviour
   Paired with sections/sticky-atc.liquid and assets/sticky-atc.css

   --------------------------------------------------------------------------
   PRIVACY DECLARATION (for the CookieYes cookie declaration)
   --------------------------------------------------------------------------
   Cookies set by this component ................ none
   localStorage keys written by this component ... none
   sessionStorage keys written by this component . none
   IndexedDB stores ............................. none

   Outbound network requests made by this component:
     none directly.

     Adding to cart is delegated to the theme's existing <product-form>
     controller (assets/theme.js, productAddButtonForm), which POSTs to the
     first-party endpoint {routes.cart}/add.js and then GETs {routes.cart}.js.
     No request from this component reaches a third-party domain.

   Analytics / advertising / marketing events fired: none.
     This component intentionally fires no GTM, Meta, Klaviyo or Postscript
     events. If such an event is ever added it MUST be placed inside
     `whenTrackingAllowed()` below, which gates on the Shopify Customer
     Privacy API. Events are discarded when consent is refused, never queued
     for replay.

   No fingerprinting: no canvas reads, no device or font enumeration, no
   cross-site identifier reads or writes.
   ========================================================================== */

const SEL = {
  root: '[data-sticky-atc]',
  form: '[data-sticky-atc-form]',
  id: '[data-sticky-atc-id]',
  thumb: '[data-sticky-atc-thumb]',
  priceCurrent: '[data-sticky-atc-price-current]',
  priceWas: '[data-sticky-atc-price-was]',
  option: '[data-sticky-atc-option]',
  quantity: '[data-sticky-atc-quantity]',
  submit: '[data-sticky-atc-submit]',
  label: '[data-sticky-atc-label]',
  error: '[data-sticky-atc-error]',
  announce: '[data-sticky-atc-announce]',
  variantJson: '[data-sticky-atc-variants]',

  // Theme-owned elements. These are read, and driven via their own events.
  mainForm: 'form[data-product-form]',
  mainButton: '[data-add-to-cart]',
  mainQuantity: '[data-quantity-input]',
  optionWrapper: '[data-split-select-wrapper]',
  singleOptionSelector: '[data-single-option-selector]',

  rebuyFlyout: '.rebuy-cart__flyout',
  focusable:
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
};

const ANNOUNCE_CLEAR_MS = 5000;

/**
 * Consent gate. Returns true only when a marketing/analytics call is lawful.
 * Currently unused — this component fires no tracking. It exists so that any
 * future event has an obvious, correct home. Do not remove.
 */
function whenTrackingAllowed(fn) {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  const mayTrack = !cp || !cp.isRegulationEnforced() || cp.userCanBeTracked();
  if (mayTrack) fn();
  // Consent refused or not yet given: discard. Never buffer for replay.
}

function isVisible(el) {
  if (!el) return false;
  if (el.hidden) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (parseFloat(style.opacity) === 0) return false;
  return el.getClientRects().length > 0;
}

class StickyAddToCart {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector(SEL.form);
    this.idInput = root.querySelector(SEL.id);
    this.thumb = root.querySelector(SEL.thumb);
    this.priceCurrent = root.querySelector(SEL.priceCurrent);
    this.priceWas = root.querySelector(SEL.priceWas);
    this.selects = Array.from(root.querySelectorAll(SEL.option));
    this.quantityInput = root.querySelector(SEL.quantity);
    this.submit = root.querySelector(SEL.submit);
    this.label = root.querySelector(SEL.label);
    this.errorEl = root.querySelector(SEL.error);
    this.announceEl = root.querySelector(SEL.announce);

    this.mainForm = document.querySelector(SEL.mainForm);
    this.mainButton = document.querySelector(SEL.mainButton);

    this.variants = this.readVariantJson();
    this.productTitle = (root.querySelector('.sticky-atc__title') || {}).textContent || '';
    this.productTitle = this.productTitle.trim();
    this.useCurrencyCode = root.dataset.currencyCode === 'true';

    this.announceTimer = null;
    this.pending = false;
    this.lastFocusBeforeDrawer = null;
    this.trapHandler = null;

    // Templates with no add-to-cart block (for example product.free-gift)
    // have no main button. There is nothing to observe and nothing to
    // delegate to, so the bar stays out of the DOM flow entirely.
    this.enabled = Boolean(this.mainForm && this.mainButton);
  }

  readVariantJson() {
    const el = this.root.querySelector(SEL.variantJson);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  init() {
    if (!this.enabled) return;

    // Captured before we touch it, so the bar's offset is additive.
    this.basePaddingBottom =
      window.getComputedStyle(document.body).paddingBottom || '0px';

    this.media = window.matchMedia(this.root.dataset.activation || 'all');
    this.mediaListener = () => this.applyVisibility();
    if (typeof this.media.addEventListener === 'function') {
      this.media.addEventListener('change', this.mediaListener);
    } else if (typeof this.media.addListener === 'function') {
      this.media.addListener(this.mediaListener);
    }

    this.observeScroll();
    this.observeHeight();
    this.bindEvents();
    this.observeMainState();
    this.observeRebuyDrawer();

    // Seed from whatever the main form currently has selected.
    const currentId = this.readMainVariantId();
    if (currentId) this.syncFromVariantId(currentId, { silent: true });
  }

  /* ---------------------------------------------------------------- state */

  readMainVariantId() {
    const input = this.mainForm && this.mainForm.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  syncFromVariantId(variantId, opts = {}) {
    const key = String(variantId);
    const variant = this.variants[key];
    if (!variant) return;

    if (this.idInput) this.idInput.value = key;

    // Selects reflect the variant's option values.
    this.selects.forEach((select, index) => {
      const value = variant.options[index];
      if (typeof value === 'string' && select.value !== value) {
        select.value = value;
      }
    });

    if (this.priceCurrent) {
      this.priceCurrent.textContent = this.useCurrencyCode
        ? variant.price_with_currency
        : variant.price;
    }

    if (this.priceWas) {
      const was = this.useCurrencyCode
        ? variant.compare_at_with_currency
        : variant.compare_at;
      if (was) {
        this.priceWas.textContent = was;
        this.priceWas.hidden = false;
      } else {
        this.priceWas.textContent = '';
        this.priceWas.hidden = true;
      }
    }

    if (this.thumb && variant.image) {
      this.thumb.src = variant.image;
    }

    this.setAvailability(variant.available);
    if (!opts.silent) this.clearError();
  }

  setAvailability(available) {
    if (!this.submit || !this.label) return;
    if (available) {
      this.submit.disabled = false;
      if (!this.pending) this.label.textContent = this.textFor('add');
    } else {
      this.submit.disabled = true;
      this.label.textContent = this.textFor('soldOut');
    }
  }

  textFor(kind) {
    // Labels come from the theme's locale files via data attributes, so the
    // bar stays translated in all six shipped locales.
    if (kind === 'add') return this.root.dataset.labelAdd || 'Add to cart';
    if (kind === 'soldOut') return this.root.dataset.labelSoldOut || 'Sold out';
    return '';
  }

  /* --------------------------------------------------------------- wiring */

  bindEvents() {
    // Main form -> bar. The theme dispatches this on every variant change.
    document.addEventListener('theme:variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      if (variant && variant.id) this.syncFromVariantId(variant.id);
    });

    // Bar -> main form.
    this.selects.forEach((select) => {
      select.addEventListener('change', () => this.pushOptionsToMainForm());
    });

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => {
        const main = document.querySelector(SEL.mainQuantity);
        if (!main) return;
        const value = Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
        this.quantityInput.value = String(value);
        main.value = String(value);
        main.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });
  }

  /**
   * Writes the bar's selections into the theme's own option controls so the
   * main form updates too, then lets the theme recompute the variant. Handles
   * both variant styles the theme renders: radio/swatch fieldsets and the
   * select-popout dropdown.
   */
  pushOptionsToMainForm() {
    this.selects.forEach((select) => {
      const position = select.dataset.optionPosition;
      const name = select.dataset.optionName;
      const value = select.value;

      const wrapper = document.querySelector(
        `${SEL.optionWrapper}[data-option-position="${position}"]`
      );
      const scope = wrapper || document;

      // Radio / swatch style.
      const radios = Array.from(
        scope.querySelectorAll('input[type="radio"][name^="options"]')
      ).filter((input) => input.name === `options[${name}]`);

      const radio = radios.find((input) => input.value === value);
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('input', { bubbles: true }));
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      // Dropdown (select-popout) style.
      const hidden = scope.querySelector(
        `${SEL.singleOptionSelector}[data-index="option${position}"]`
      );
      if (hidden) {
        hidden.value = value;
        hidden.dispatchEvent(new Event('input', { bubbles: true }));
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // If the theme did not pick the change up, fall back to setting the id
    // directly so the cart still receives the variant the shopper chose.
    window.setTimeout(() => {
      const expected = this.resolveVariantIdFromSelects();
      if (!expected) return;
      const mainId = this.mainForm.querySelector('input[name="id"]');
      if (mainId && mainId.value !== String(expected)) {
        mainId.value = String(expected);
      }
      this.syncFromVariantId(expected);
    }, 0);
  }

  resolveVariantIdFromSelects() {
    const chosen = this.selects.map((select) => select.value);
    const match = Object.keys(this.variants).find((id) => {
      const options = this.variants[id].options;
      return chosen.every((value, index) => options[index] === value);
    });
    return match || null;
  }

  /* ------------------------------------------------------------ visibility */

  /**
   * Scroll-threshold reveal, mirroring the behaviour of the app this replaces.
   *
   * Note this is deliberately NOT an IntersectionObserver on the main button.
   * The theme pins the product form column (.product-single .is-sticky,
   * position: sticky, top: 30px, all breakpoints), so the real add to cart
   * button almost never leaves the viewport and an observer-based reveal
   * would fire only once the entire product section had scrolled past.
   */
  observeScroll() {
    this.threshold = parseInt(this.root.dataset.threshold, 10) || 600;
    this.pastThreshold = false;

    let ticking = false;
    this.onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        this.pastThreshold = window.scrollY >= this.threshold;
        this.applyVisibility();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.onScroll();
  }

  /**
   * The bar may share the screen with the main button, but must never cover
   * it. If the button sits inside the strip the bar occupies, stay hidden.
   */
  overlapsMainButton() {
    const height = this.lastHeight || 0;
    if (!height) return false;
    const rect = this.mainButton.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    return rect.bottom > window.innerHeight - height && rect.top < window.innerHeight;
  }

  applyVisibility() {
    const shouldShow =
      this.pastThreshold && this.media.matches && !this.overlapsMainButton();
    if (shouldShow === this.shown) return;
    this.shown = shouldShow;

    if (shouldShow) {
      this.root.hidden = false;
      // Focus is never moved to the bar when it appears (WCAG 3.2.1).
      this.root.setAttribute('data-state', 'entering');
      window.requestAnimationFrame(() => {
        this.root.removeAttribute('data-state');
        this.measure();
      });
    } else {
      this.root.hidden = true;
      this.setOffset(0);
    }
  }

  observeHeight() {
    // Never hardcode a body offset: the bar is materially taller on mobile.
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.root);
  }

  measure() {
    if (this.root.hidden) return this.setOffset(0);
    const height = this.root.getBoundingClientRect().height;
    // Retained while hidden so the overlap guard has a height to work with.
    if (height) this.lastHeight = height;
    this.setOffset(height);
    // Now that a real height is known, the overlap guard can be re-evaluated.
    this.applyVisibility();
  }

  setOffset(height) {
    const value = `${Math.round(height)}px`;
    document.documentElement.style.setProperty('--sticky-atc-height', value);
    this.root.style.setProperty('--sticky-atc-height', value);
    // Added to whatever bottom padding the theme already applies, rather than
    // replacing it, so no layout is lost while the bar is on screen.
    document.body.style.paddingBottom = height
      ? `calc(${this.basePaddingBottom} + ${value})`
      : '';
  }

  /* ------------------------------------------------------------ add to cart */

  addToCart() {
    if (this.pending || this.submit.disabled) return;

    this.clearError();
    this.setPending(true);

    // Delegating to the theme's own button keeps one cart implementation:
    // it builds FormData from the real product form (so a quantity set on
    // the main form is respected), POSTs to /cart/add.js, refreshes the
    // header total and opens the Rebuy drawer.
    this.mainButton.click();
  }

  setPending(pending) {
    this.pending = pending;
    if (pending) {
      // aria-disabled, not disabled: the button keeps its accessible name
      // and stays in the tab order. The label text is never replaced.
      this.submit.setAttribute('aria-disabled', 'true');
      this.submit.setAttribute('aria-busy', 'true');
    } else {
      this.submit.removeAttribute('aria-disabled');
      this.submit.removeAttribute('aria-busy');
    }
  }

  /**
   * Mirrors the theme controller's state onto the bar. The theme toggles
   * `loading` / `has-success` on the main button and writes failures into
   * [data-error-display].
   */
  observeMainState() {
    const classObserver = new MutationObserver(() => {
      const classes = this.mainButton.classList;
      if (classes.contains('loading')) {
        this.setPending(true);
        return;
      }
      if (this.pending) {
        this.setPending(false);
        if (classes.contains('has-success')) this.handleSuccess();
      }
    });
    classObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const errorDisplay = document.querySelector('[data-error-display]');
    if (errorDisplay) {
      const errorObserver = new MutationObserver(() => {
        const text = errorDisplay.textContent.trim();
        if (text && this.shown) this.showError(text);
      });
      errorObserver.observe(errorDisplay, { childList: true, subtree: true });
    }
  }

  handleSuccess() {
    const variant = this.variants[String(this.idInput.value)];
    const parts = [this.productTitle];
    if (variant && variant.title && variant.title !== 'Default Title') {
      parts.push(variant.title);
    }
    const quantity = this.quantityInput
      ? Math.max(1, parseInt(this.quantityInput.value, 10) || 1)
      : null;
    if (quantity && quantity > 1) parts.push(`quantity ${quantity}`);

    this.announce(`${parts.join(', ')}, added to cart`);
  }

  announce(message) {
    if (!this.announceEl) return;
    window.clearTimeout(this.announceTimer);
    this.announceEl.textContent = message;
    // Cleared so it is not re-announced on a later, unrelated update.
    this.announceTimer = window.setTimeout(() => {
      this.announceEl.textContent = '';
    }, ANNOUNCE_CLEAR_MS);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    // role is set at the moment of failure so the alert fires reliably.
    this.errorEl.setAttribute('role', 'alert');
    // The button stays enabled so the shopper can retry.
    this.setPending(false);
    this.measure();
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
    this.errorEl.removeAttribute('role');
  }

  /* ------------------------------------------------------- drawer focus */

  observeRebuyDrawer() {
    const watch = (flyout) => {
      let wasOpen = isVisible(flyout);
      const check = () => {
        const open = isVisible(flyout);
        if (open === wasOpen) return;
        wasOpen = open;
        if (open) {
          this.trapFocusIn(flyout);
        } else {
          this.releaseFocus();
        }
      };
      new MutationObserver(check).observe(flyout, {
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden', 'hidden'],
      });
      // Rebuy re-renders its flyout contents; catch open states that arrive
      // as a subtree replacement rather than an attribute flip.
      new MutationObserver(check).observe(flyout, {
        childList: true,
        subtree: true,
      });
    };

    const existing = document.querySelector(SEL.rebuyFlyout);
    if (existing) return watch(existing);

    const bodyObserver = new MutationObserver(() => {
      const flyout = document.querySelector(SEL.rebuyFlyout);
      if (flyout) {
        bodyObserver.disconnect();
        watch(flyout);
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  trapFocusIn(container) {
    // Only take over focus when the bar started this add. If the shopper
    // opened the cart from the header, leave the theme's own behaviour alone.
    if (!this.shown) return;

    this.lastFocusBeforeDrawer = this.submit;

    const focusables = () =>
      Array.from(container.querySelectorAll(SEL.focusable)).filter(isVisible);

    window.requestAnimationFrame(() => {
      const items = focusables();
      if (items.length) items[0].focus();
    });

    this.trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.trapHandler, true);
  }

  releaseFocus() {
    if (this.trapHandler) {
      document.removeEventListener('keydown', this.trapHandler, true);
      this.trapHandler = null;
    }
    if (this.lastFocusBeforeDrawer && isVisible(this.lastFocusBeforeDrawer)) {
      this.lastFocusBeforeDrawer.focus();
    }
    this.lastFocusBeforeDrawer = null;
  }
}

function boot() {
  document.querySelectorAll(SEL.root).forEach((root) => {
    if (root.dataset.stickyAtcReady) return;
    root.dataset.stickyAtcReady = 'true';
    new StickyAddToCart(root).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Theme editor: re-init when the section is re-rendered.
document.addEventListener('shopify:section:load', boot);

export { StickyAddToCart, whenTrackingAllowed };
